import React, {useState, useEffect, useCallback, useMemo, useContext} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faUpload} from "@fortawesome/free-solid-svg-icons";
import JSZip from "jszip";

// Utils
import useApi from "../../hooks/useApi.js";
import {FacilityNavContext} from "../../contexts/facilitynav.js";

// Components
import Modal from "../../components/Modal.js";
import ModalFileView from "../facility/ModalFileView.js";

// Style
import {pad} from "../../style/components/variables.js";
import {voice} from "../../style/components/typography.js";
import {flex} from "../../style/components/mixins.js";
import {Anchor, Button, Heading, Inline, Text} from "../../style/components/general.js";
import {getAncestryName, getIndexedBuilderFields} from "../checksheet-builder/helpers.js";
import {FIELD, GROUP, mergeBuilders} from "../../utils/builder.js";

const extensionMap = {
  "text/plain": "txt",
  pdf: "pdf",
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "application/pdf": "pdf",
  "application/msword": "doc",
  "text/csv": "csv",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-outlook": "msg"
};

const ModalAttachments = ({
  visible,
  setVisible,
  builder,
  files,
  pendingNotes,
  resolvedNotes,
  stageName,
  hasBackButton,
  goBack
}) => {
  const {api: fileApi} = useApi("files");

  const {facility} = useContext(FacilityNavContext);

  const [consolidated, setConsolidated] = useState(null);
  const [links, setLinks] = useState(null);
  const [fileViewVisible, setFileViewVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileCt, setFileCt] = useState(0);

  const mergedBuilder = useMemo(
    () => mergeBuilders(builder, facility.builder),
    [builder, facility]
  );

  const indexedFields = useMemo(() => {
    const result = {};
    return mergedBuilder
      ? getIndexedBuilderFields(mergedBuilder.allIds, mergedBuilder, result)
      : {};
  }, [mergedBuilder]);

  const canPreview = file =>
    file.extension.name.includes("image") ||
    file.extension.name.includes("pdf") ||
    file.extension.name === "text/plain";

  useEffect(() => {
    if (!links && consolidated)
      fileApi
        .callGet(null, {
          ids: consolidated.flatMap(({files: consolidatedFiles}) =>
            consolidatedFiles.map(({id}) => id)
          )
        })
        .then(({status, data}) => {
          if (status === 200 && data) setLinks(data.links);
        });
  }, [fileApi, links, consolidated]);

  const getDisplayName = useCallback(
    element => {
      let label = stageName;
      if (element) {
        const {label: elementLabel} = element;
        label = `${label} ${elementLabel}`;
      }
      return label;
    },
    [stageName]
  );

  useEffect(() => {
    const attachments = {};

    let ct = 0;

    if (indexedFields) {
      // Field Files
      Object.keys(files).map(key => {
        files[key].files.map(file => {
          ct++;
          if (attachments[key]) attachments[key].files.push(file);
          else
            attachments[key] = {
              name: stageName
                ? `${getDisplayName(mergedBuilder.byId[key])}:`
                : `${getAncestryName(mergedBuilder.byId[key], mergedBuilder)}:`,
              files: [file],
              type: FIELD
            };
        });
      });

      // Note Files
      Object.keys(pendingNotes).map(key => {
        pendingNotes[key].map(note => {
          if (note.files)
            note.files.map(file => {
              ct++;

              if (attachments[key]) attachments[key].files.push(file);
              else
                attachments[key] = {
                  name: stageName
                    ? `${getDisplayName(mergedBuilder.byId[key])}:`
                    : `${getAncestryName(mergedBuilder.byId[key], mergedBuilder)}:`,
                  files: [file]
                };
            });
        });
      });

      Object.keys(resolvedNotes).map(key => {
        resolvedNotes[key].map(note => {
          if (note.files)
            note.files.map(file => {
              ct++;

              if (attachments[key]) attachments[key].files.push(file);
              else
                attachments[key] = {
                  name: stageName
                    ? `${getDisplayName(mergedBuilder.byId[key])}:`
                    : `${getAncestryName(mergedBuilder.byId[key], mergedBuilder)}:`,
                  files: [file]
                };
            });
        });
      });

      if (
        Object.keys(attachments).length === 1 &&
        Object.values(attachments)[0]?.length === 1 &&
        attachments.values()[0].files
      )
        setSelectedFile(attachments.values()[0].files[0]);

      setFileCt(ct);

      setConsolidated(
        Object.entries(attachments)
          .sort(([keyA], [keyB]) => {
            const elementA = mergedBuilder.byId[keyA];
            const elementB = mergedBuilder.byId[keyB];

            if (elementA.element === GROUP && elementB.element === GROUP) {
              const indexA = mergedBuilder.allIds.indexOf(keyA);
              const indexB = mergedBuilder.allIds.indexOf(keyB);

              return indexA - indexB;
            }

            if (elementA.element === GROUP) return 1;
            if (elementB.element === GROUP) return -1;

            const fieldIndexA = indexedFields[keyA];
            const fieldIndexB = indexedFields[keyB];

            return fieldIndexA - fieldIndexB;
          })
          .map(([, val]) => val)
      );
    }
  }, [mergedBuilder, files, getDisplayName, pendingNotes, resolvedNotes, stageName, indexedFields]);

  const openFile = file => {
    fileApi.callGet(file.id, {download: true}).then(({status, data}) => {
      if (status === 200 && data)
        setSelectedFile({...file, link: data.link, download: data.download});
    });
  };

  // Open single file preview
  useEffect(() => {
    if (selectedFile) setFileViewVisible(true);
  }, [selectedFile]);

  const getExtension = file => {
    if (file?.extension?.name) {
      const abbreviated = extensionMap[file.extension.name];
      if (abbreviated) return ` (${abbreviated})`;
    }
    return "";
  };

  const downloadBlobAsFile = blob => {
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "files.zip";
    document.body.appendChild(a); // required for iOS Safari

    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 0);
  };

  const getUniqueFileName = (name, existing) => {
    let base = name;
    let ext = "";
    const match = name.match(/(.*?)(\.[^.]+)$/);
    if (match) [, base, ext] = match;

    let counter = 1;
    let finalName = name;
    while (existing.has(finalName)) {
      finalName = `${base} (${counter++})${ext}`;
    }

    existing.add(finalName);
    return finalName;
  };

  const downloadFilesAsZip = async f => {
    const zip = new JSZip();

    const uniqueNames = new Set();

    // Step 1: Download all file blobs in parallel
    const blobs = await Promise.all(
      f.map(file =>
        fetch(links[file.id])
          .then(res => res.blob())
          .then(blob => ({name: getUniqueFileName(file.label, uniqueNames), blob}))
      )
    );

    // Step 2: Add all blobs to the ZIP file
    blobs.forEach(({name, blob}) => {
      zip.file(name, blob);
    });

    // Step 3: Generate the ZIP blob
    const content = await zip.generateAsync({type: "blob"});

    // Step 4: Download the ZIP blob
    downloadBlobAsFile(content);
  };

  if (consolidated && selectedFile)
    return (
      <ModalFileView
        visible={fileViewVisible}
        setVisible={setVisible}
        hasBackButton={hasBackButton || fileCt > 1}
        goBack={() => setSelectedFile(null)}
        file={selectedFile}
      />
    );

  return (
    <Modal visible={visible} setVisible={setVisible} hasBackButton={hasBackButton} goBack={goBack}>
      <Heading>Attachments</Heading>
      {consolidated &&
        consolidated.map(({name, files: consolidatedFiles, type}) => (
          <div key={name}>
            <Inline>
              {type === FIELD && (
                <Icon>
                  <FontAwesomeIcon icon={faUpload} />
                </Icon>
              )}
              <NotesHeader type={type}>{name}</NotesHeader>
            </Inline>
            {consolidatedFiles.map(file => (
              <Attachment key={file.id}>
                {canPreview(file) || !links || !links[file.id] ? (
                  <FileLink onClick={() => openFile(file)}>
                    {file.label}
                    {getExtension(file)}
                  </FileLink>
                ) : (
                  <FileDownload href={links[file.id]} download>
                    {file.label}
                    {getExtension(file)}
                  </FileDownload>
                )}
              </Attachment>
            ))}
          </div>
        ))}
      {links && (
        <>
          <br />
          <Button
            onClick={() =>
              downloadFilesAsZip(
                consolidated.flatMap(({files: consolidatedFiles}) => consolidatedFiles)
              )
            }>
            Download All
          </Button>
        </>
      )}
    </Modal>
  );
};

ModalAttachments.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  builder: PropTypes.objectOf(PropTypes.any),
  files: PropTypes.objectOf(PropTypes.any),
  pendingNotes: PropTypes.objectOf(PropTypes.any),
  resolvedNotes: PropTypes.objectOf(PropTypes.any),
  stageName: PropTypes.string,
  hasBackButton: PropTypes.bool,
  goBack: PropTypes.func
};

ModalAttachments.defaultProps = {
  builder: {allIds: [], byId: {}},
  files: null,
  pendingNotes: null,
  resolvedNotes: null,
  stageName: null,
  hasBackButton: false,
  goBack: () => {}
};

// Style Overrides
const Attachment = styled.div`
  ${flex("row", "no-wrap", "start", "center")};
  margin-right: ${pad}px;
`;

const NotesHeader = styled(Text)`
  ${voice.normal};
  font-weight: bold;
  text-transform: uppercase;
  margin-top: ${pad}px;

  ${({type, theme}) =>
    type === FIELD &&
    css`
      color: ${theme.field};
    `}
`;

const FileLink = styled(Button)`
  ${voice.weak};
  font-weight: bold;
  background: none;
  color: ${props => props.theme.secondary};
  cursor: pointer;

  text-decoration: underline;
`;

const FileDownload = styled(Anchor)`
  ${voice.weak};
  font-weight: bold;
  background: none;
  color: ${props => props.theme.secondary};
  cursor: pointer;

  text-decoration: underline;
`;

const Icon = styled.div`
  margin-top: 10px;
  margin-right: -3px;

  svg {
    fill: ${({theme}) => theme.field};
  }
`;

export default ModalAttachments;
